// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import {
  type EvolveApiRoute,
  type EvolveApiResponse,
  type EvolveApiParams,
  ignoreCanceledApiRequest,
} from '@evolvemep/api-ts';
import { useDebouncedValue } from '@mantine/hooks';

import type { WithRequiredIsh, KeysOfType } from 'helpers/helper-types';

import type { SearchableMultiSelectorProps } from './types';

export const useSearchableData = <
  Route extends EvolveApiRoute<'GET_PAGINATED'>,
  T extends Partial<EvolveApiResponse<'GET_PAGINATED', Route>['data'][number]>,
  TKey extends KeysOfType<T, string>,
  TLabel extends ReactNode,
>({
  paginatedGet,
  searchKey,

  useLike = !!searchKey,
  lazy = false,
  excludeOption,
}: { lazy?: boolean } & WithRequiredIsh<
  Pick<SearchableMultiSelectorProps<Route, T, TKey, TLabel>, 'paginatedGet' | 'searchKey' | 'useLike' | 'excludeOption'>
>) => {
  const [isLazy, setIsLazyInner] = useState(lazy);
  const [loadingFresh, setLoadingFresh] = useState(!isLazy);
  const [pendingSearchPhrase, setPendingSearchPhrase] = useState('');
  const [searchPhrase] = useDebouncedValue(pendingSearchPhrase, 400);

  const { data: rawOptions, loading, reset, fetchNextPage: fetchNextPageNoParams, entireCount } = paginatedGet;

  const options = useMemo(
    () => (rawOptions as unknown as T[]).filter((o) => !excludeOption?.(o)),
    [excludeOption, rawOptions],
  );

  const fetchNextPage = useCallback(async () => {
    const params = (
      searchPhrase.length > 0
        ? {
            [searchKey ?? 'searchPhrase']: `${useLike ? 'like:' : ''}${encodeURIComponent(searchPhrase)}`,
          }
        : {}
    ) as Partial<EvolveApiParams<'GET_PAGINATED', Route>>;
    await fetchNextPageNoParams({ params });
  }, [fetchNextPageNoParams, searchKey, searchPhrase, useLike]);

  useEffect(() => {
    // If the `paginatedGet` is reset externally, `entireCount` will become `undefined`,
    // signaling that we need to fetch a page.
    if (!isLazy && !loading && !loadingFresh && entireCount === undefined) {
      void fetchNextPage();
    }
  }, [entireCount, fetchNextPage, isLazy, loading, loadingFresh]);

  useEffect(() => {
    if (!isLazy) {
      reset();
      void fetchNextPage()
        .catch(ignoreCanceledApiRequest)
        .finally(() => setLoadingFresh(false));
    }
  }, [fetchNextPage, isLazy, reset]);

  const setIsLazy = useCallback((newIsLazy: boolean) => {
    setIsLazyInner((currentLazy) => {
      if (!newIsLazy && currentLazy !== newIsLazy) {
        setLoadingFresh(true);
      }
      return newIsLazy;
    });
  }, []);

  return useMemo(
    () => ({
      entireCount,
      options,
      fetchNextPage,
      loading,
      loadingFresh,
      pendingSearchPhrase,
      searchPhrase,
      setSearchPhrase: setPendingSearchPhrase,
      setIsLazy,
    }),
    [
      entireCount,
      fetchNextPage,
      loading,
      loadingFresh,
      options,
      pendingSearchPhrase,
      searchPhrase,
      setIsLazy,
      setPendingSearchPhrase,
    ],
  );
};
